import { Button } from 'components/button';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop, {
    Crop,
    centerCrop,
    convertToPixelCrop,
    makeAspectCrop,
} from 'react-image-crop';
import { canvasPreview } from 'utilities/canvasPreview';

const ASPECT_RATIO = 1;
const MIN_DIMENSION = 150;

const ImageCropper = ({ updateAvatar, setModalOpen, img }: any) => {
    const { t } = useTranslation();
    const imgRef = useRef<HTMLImageElement | null>(null);
    const previewCanvasRef = useRef<HTMLCanvasElement>(null);
    const [imgSrc, setImgSrc] = useState(img || '');
    const [crop, setCrop] = useState<Crop>();
    const [error, setError] = useState('');

    const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        setImgSrc('');
        const file = e.target.files?.[0];
        if (!file) return;
        setCrop(undefined);
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            const imageElement = new Image();
            const imageUrl = reader.result?.toString() || '';
            imageElement.src = imageUrl;

            imageElement.addEventListener('load', (ev) => {
                if (error) setError('');

                const { naturalWidth, naturalHeight } =
                    ev.currentTarget as HTMLImageElement;

                if (
                    naturalWidth < MIN_DIMENSION ||
                    naturalHeight < MIN_DIMENSION
                ) {
                    setError(t('Image must be at least 150 x 150 pixels.'));
                    return setImgSrc('');
                }
                return null;
            });
            setImgSrc(imageUrl);
        });
        reader.readAsDataURL(file);
    };

    const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const { width, height } = e.currentTarget;
        const cropWidthInPercent = ((MIN_DIMENSION + 120) / width) * 100;

        const aspectCrop = makeAspectCrop(
            {
                unit: '%',
                width: cropWidthInPercent,
            },
            ASPECT_RATIO,
            width,
            height
        );
        const centeredCrop = centerCrop(aspectCrop, width, height);
        setCrop(centeredCrop);
    };

    const handleImageCrop = () => {
        if (!imgRef.current || !previewCanvasRef.current || !crop) return;
        canvasPreview(
            imgRef.current,
            previewCanvasRef.current,
            convertToPixelCrop(
                crop,
                imgRef.current?.width,
                imgRef.current?.height
            )
        );
        const dataUrl = previewCanvasRef?.current.toDataURL();
        updateAvatar(dataUrl);
        setModalOpen();
    };

    return (
        <div>
            {error && <p className="text-red-400 text-xs">{error}</p>}
            {imgSrc && (
                <div className="flex flex-col items-center">
                    <ReactCrop
                        crop={crop}
                        onChange={(percentCrop) => setCrop(percentCrop)}
                        circularCrop
                        keepSelection
                        aspect={ASPECT_RATIO}
                        minWidth={MIN_DIMENSION}
                    >
                        <img
                            ref={imgRef}
                            src={imgSrc}
                            alt="Upload"
                            style={{ maxHeight: '50vh' }}
                            onLoad={onImageLoad}
                        />
                    </ReactCrop>
                    {/* <div className="mt-3">
                        <label
                            htmlFor="upload-photo"
                            className="cursor-pointer"
                        >
                            <div className="bg-gray-300 text-black text-md py-2 px-4 rounded-md shadow-sm hover:bg-gray-400 transition-colors">
                                Choose another image
                            </div>
                        </label>
                        <input
                            type="file"
                            id="upload-photo"
                            className="hidden"
                            accept="image/*"
                            onChange={onSelectFile}
                        />
                    </div> */}
                    <label className="block mb-3 w-fit">
                        <span className="sr-only">
                            {t('Choose profile photo')}
                        </span>
                    </label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={onSelectFile}
                        className="block w-full text-sm text-slate-500 file:me-4 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-xs file:bg-gray-700 file:text-sky-300 hover:file:bg-gray-600"
                    />
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            size="small"
                            variant="secondaryOutline"
                            onClick={() => setModalOpen(false)}
                        >
                            {t('Cancel')}
                        </Button>
                        <Button
                            type="button"
                            size="small"
                            variant="secondary"
                            onClick={handleImageCrop}
                        >
                            {t('Done & Save')}
                        </Button>
                    </div>
                </div>
            )}
            {crop && (
                <canvas
                    ref={previewCanvasRef}
                    className="mt-4"
                    style={{
                        display: 'none',
                        border: '1px solid black',
                        objectFit: 'contain',
                        width: 150,
                        height: 150,
                    }}
                />
            )}
        </div>
    );
};
export default ImageCropper;
